import React, { useState, useEffect } from "react";
import { Button, Checkbox, Icon, Input, Label, Modal, Table } from "semantic-ui-react";
import { map, size } from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import { ModalConfirm } from "../../Common";
import { useAuth, useSiteConfig } from "../../../hooks";
import { printTipsReport } from "../../../utils/tipsReport";
import {
  getSessionTipsApi,
  previewTipsApi,
  settleTipsApi,
  voidTipsApi,
} from "../../../api/tips";
import { positionText, tipModelText } from "../../../utils/tips";

const money = (value) => `S/ ${Number(value || 0).toFixed(2)}`;

// Caja → Apertura y cierre → "Propinas": reparte lo cobrado en el turno según
// la regla del restaurante (Configuración → Propinas) y paga desde el cajón con
// un egreso por persona. Solo toma lo aún no liquidado (pulsar dos veces no
// paga dos veces). Un Admin puede anular una liquidación mientras la caja siga
// abierta. Ver docs/claude/reglas-de-negocio.md (#33).
export function TipsSettlement(props) {
  const { session, onSessionChange } = props;
  const { auth } = useAuth();
  const token = auth?.token;
  const isAdmin = !!auth?.me?.is_staff;
  const { siteConfig, getSiteConfig } = useSiteConfig();
  const [data, setData] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState("");

  const load = async () => {
    try {
      const result = await getSessionTipsApi(session.id, token);
      setData(result);
      setChosen(map(result.people.filter((person) => person.suggested), "id"));
      setPreview(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Se recarga cuando cambian las propinas por liquidar (cobros nuevos, liquidación).
  const pendingKey = `${session.id}-${session.summary?.tips_pending_count}-${session.summary?.tips_paid_drawer}`;
  useEffect(() => {
    load();
  }, [pendingKey]);
  useEffect(() => {
    getSiteConfig();
  }, []);

  if (!data) return null;
  const pending = data.pending;
  const hasPending = pending.count > 0;
  if (!hasPending && size(data.settlements) === 0) return null;

  const toggle = (id, checked) => {
    setPreview(null);
    setChosen((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const onPreview = () => run(async () => setPreview(await previewTipsApi(session.id, chosen, token)));

  const onSettle = () =>
    run(async () => {
      onSessionChange(await settleTipsApi(session.id, chosen, token));
      setPreview(null);
      toast.success("Propinas liquidadas");
    });

  const onVoid = () =>
    run(async () => {
      onSessionChange(await voidTipsApi(session.id, voidTarget.id, voidReason.trim(), token));
      setVoidTarget(null);
      setVoidReason("");
      toast.success("Liquidación anulada");
    });

  const config = data.config;
  const drawerTotal = preview
    ? preview.shares.reduce((sum, row) => sum + Number(row.drawer), 0)
    : 0;

  return (
    <div className="cash-admin__card">
      <h3>Propinas</h3>
      <p className="cash-admin__muted">
        Regla: {tipModelText(config.model)}
        {Number(config.card_fee_percent) > 0 && ` · comisión tarjeta ${Number(config.card_fee_percent)}%`}
        {Number(config.retention_percent) > 0 && ` · retención ${Number(config.retention_percent)}%`}
        {" · "}
        {config.card_payout === "PAYABLE"
          ? "la de tarjeta queda por pagar"
          : "la de tarjeta se paga desde la caja"}
        . {isAdmin ? "Se cambia en Configuración → Propinas." : ""}
      </p>

      {hasPending ? (
        <>
          <p>
            Por liquidar: <strong>{money(pending.cash)}</strong> en efectivo y{" "}
            <strong>{money(pending.card)}</strong> en tarjeta ({pending.count} cuenta(s)).
          </p>
          <label>Participan en el reparto</label>
          {size(data.people) === 0 ? (
            <p className="cash-admin__muted">
              No hay personas registradas. Créalas en Configuración → Usuarios.
            </p>
          ) : (
            // Tres columnas alineadas: nombre (con su casilla), puesto y, según la regla,
            // los puntos o el % del puesto.
            <div
              className="cash-admin__people"
              style={{
                display: "grid",
                gridTemplateColumns: "auto auto auto",
                columnGap: 16,
                rowGap: 6,
                justifyContent: "start",
                alignItems: "center",
              }}
            >
              {map(data.people, (person) => (
                <React.Fragment key={person.id}>
                  <Checkbox
                    label={person.name}
                    checked={chosen.includes(person.id)}
                    onChange={(_, d) => toggle(person.id, d.checked)}
                  />
                  <span>{positionText(person.position)}</span>
                  <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {config.model === "POINTS"
                      ? `${Number(person.points)} ${Number(person.points) === 1 ? "pt" : "pts"}`
                      : config.model === "PERCENT"
                      ? `${Number(person.percent)} %`
                      : ""}
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}
          {config.model === "INDIVIDUAL" && (
            <p className="cash-admin__muted">
              Cada mesero recibe lo de sus mesas aunque no esté marcado; lo que no tiene
              mesero se reparte entre los marcados.
            </p>
          )}
          <Button basic loading={busy} onClick={onPreview}>
            <Icon name="calculator" /> Calcular reparto
          </Button>

        </>
      ) : (
        <p className="cash-admin__muted">No hay propinas por liquidar.</p>
      )}

      {size(data.settlements) > 0 && (
        <Table compact unstackable>
          <Table.Body>
            {map(data.settlements, (s) => (
              <Table.Row key={s.id} disabled={!!s.voided_at}>
                <Table.Cell>
                  {moment.parseZone(s.created_at).format("HH:mm")}
                  {s.voided_at && (
                    <div>
                      <Label size="mini" color="red">
                        Anulada
                      </Label>
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {/* Nombre y monto en columnas, con los montos alineados a la derecha. */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto auto",
                      columnGap: 14,
                      justifyContent: "start",
                    }}
                  >
                    {map(s.shares, (share) => (
                      <React.Fragment key={share.id}>
                        <span>{share.name}</span>
                        <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {money(Number(share.cash_amount) + Number(share.card_amount))}
                          {s.card_payout === "PAYABLE" && Number(share.card_amount) > 0 && (
                            <span className="cash-admin__muted">
                              {" "}
                              ({money(share.card_amount)} de tarjeta {share.card_paid_at ? "pagada" : "por pagar"})
                            </span>
                          )}
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                  {s.voided_at && <div className="cash-admin__muted">{s.void_reason}</div>}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <Button
                    basic
                    size="mini"
                    icon="print"
                    title="Imprimir la liquidación"
                    aria-label="Imprimir la liquidación"
                    onClick={() =>
                      printTipsReport({ siteName: siteConfig?.site_name, session, settlement: s })
                    }
                  />
                  {isAdmin && !s.voided_at && (
                    <Button
                      basic
                      size="mini"
                      onClick={() => {
                        setVoidTarget(s);
                        setVoidReason("");
                      }}
                    >
                      Anular
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Vista previa del reparto: también es la confirmación (Liquidar / Cancelar). */}
      <Modal open={!!preview} onClose={() => !busy && setPreview(null)} size="small">
        <Modal.Header>Reparto de propinas</Modal.Header>
        {preview && (
          <Modal.Content>
            <Table compact unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Persona</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Efectivo</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Tarjeta</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Sale del cajón</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {map(preview.shares, (row) => (
                  <Table.Row key={row.user}>
                    <Table.Cell>{row.name}</Table.Cell>
                    <Table.Cell textAlign="right">{money(row.cash)}</Table.Cell>
                    <Table.Cell textAlign="right">{money(row.card)}</Table.Cell>
                    <Table.Cell textAlign="right">{money(row.drawer)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            <p className="cash-admin__muted">
              Recaudado: {money(preview.totals.cash_gross)} efectivo + {money(preview.totals.card_gross)}{" "}
              tarjeta
              {Number(preview.totals.card_fee) > 0 && ` · comisión −${money(preview.totals.card_fee)}`}
              {Number(preview.totals.retention_cash) + Number(preview.totals.retention_card) > 0 &&
                ` · retención −${money(
                  Number(preview.totals.retention_cash) + Number(preview.totals.retention_card)
                )}`}
              .
            </p>
            <p>
              Sale del cajón en total: <strong>{money(drawerTotal)}</strong>. Al liquidar se registra un
              egreso por persona; entrega a cada uno lo que indica la tabla.
            </p>
          </Modal.Content>
        )}
        <Modal.Actions>
          <Button disabled={busy} onClick={() => setPreview(null)}>
            Cancelar
          </Button>
          <Button primary loading={busy} onClick={onSettle}>
            <Icon name="check" /> Liquidar
          </Button>
        </Modal.Actions>
      </Modal>

      <ModalConfirm
        title="¿Anular esta liquidación?"
        content={
          <div>
            <p>
              Se anulan sus egresos y las propinas vuelven a quedar por liquidar. Hazlo solo si el
              dinero no se entregó o ya se devolvió al cajón.
            </p>
            <Input
              fluid
              placeholder="Motivo de la anulación"
              maxLength={200}
              value={voidReason}
              onChange={(_, d) => setVoidReason(d.value)}
            />
          </div>
        }
        show={!!voidTarget}
        onCloseText="Cancelar"
        onClose={() => setVoidTarget(null)}
        onConfirmText="Anular liquidación"
        confirmDisabled={!voidReason.trim()}
        onConfirm={onVoid}
      />
    </div>
  );
}
